import './settings.scss';
import React, {FormEvent, useCallback, useState} from 'react';
import {DefaultRootState, useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Form} from 'react-bootstrap';
import {changeSettings} from './duck/settings-actions';
import {useLoadVersionsRequest} from '../search/search-hooks';
import {ToggleSlider} from './toggleSlider';
import {CONTAINER_VERSION} from '../config';
import {MessageBox} from '../message-box/message-box';
import {ContainerAttributes, ContainersResponse, MESSAGE_BOX_SETTINGS_ID} from './duck/settings-types';
import {useAuthContext} from '../auth/AuthContextProvider';
import {HibuTextButton} from '../common/hibuTextButton/HibuTextButton';
import {
    useStateContainerName,
    useStateFieldSpecificSearch,
    useStateManualValidation,
    useStateTechnicalDetails
} from './duck/settings-hooks';
import {useSettingsConfig} from '../config/config-hooks';
import {clearAllSelectedFilters} from '../facets/duck/filter.actions';
import {useAreFiltersPersistent} from '../facets/duck/filter.hooks';

export const SettingsPage = React.memo((): React.JSX.Element => {
    const settingsConfig = useSettingsConfig();
    const navigate = useNavigate();
    const location = useLocation();
    if (!settingsConfig.enabled) {
        navigate('/');
    }
    const [validated, setValidated] = useState(false);
    const settingsContainerName = useStateContainerName();
    const settingsTechnicalDetails = useStateTechnicalDetails();
    const settingsManualValidation = useStateManualValidation();
    const settingsFieldSpecificSearch = useStateFieldSpecificSearch();
    const isPersistent: boolean = useAreFiltersPersistent();

    const [indeksoVersion, setIndeksoVersion] = useState();
    const indeksoUi = CONTAINER_VERSION;
    const [indeksoUiBackendVersion, setIndeksoUiBackendVersion] = useState();
    const [containerName, setContainerName] = useState(settingsContainerName);
    const [showTechnicalDetails, setShowTechnicalDetails] = useState(settingsTechnicalDetails);
    const [showManualValidation, setShowManualValidation] = useState(settingsManualValidation);
    const [showFieldSpecificSearch, setShowFieldSpecificSearch] = useState(settingsFieldSpecificSearch);
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const {userId} = useAuthContext();
    const containers = useSelector((state: DefaultRootState) => state.settings.containers);
    const extractVersions = useCallback((versionResponse: ContainersResponse) => {
        setIndeksoVersion(versionResponse['indekso.fullVersion']);
        setIndeksoUiBackendVersion(versionResponse['hibu-ui-backend.version']);
    }, []);

    useLoadVersionsRequest(extractVersions);

    function navigateBack() {
        const doesAnyHistoryEntryExist = location.key !== 'default';
        if (doesAnyHistoryEntryExist) {
            const previousUrl = new URL(location.state?.previousPath, window.location.origin);
            if (!showFieldSpecificSearch) {
                // user disabled field specific search actively, so remove corresponding parameter from URL
                previousUrl.searchParams.delete('fs'); // prevents 'infinite loop'
            }
            navigate(previousUrl.pathname + previousUrl.search + previousUrl.hash);
        } else {
            navigate('/');
        }
    }

    const onGoBack = () => {
        navigateBack();
    };

    const getContent = (): React.JSX.Element => {
        const onSubmit = (event: FormEvent) => {
            event.preventDefault();
            if (!validated) {
                event.stopPropagation();
            }
            setValidated(true);
            dispatch(
                changeSettings(containerName, showTechnicalDetails, showManualValidation, showFieldSpecificSearch),
            );
            if (!isPersistent) {
                dispatch(clearAllSelectedFilters());
            }
            navigateBack();
        };

        const configToggles = settingsConfig.toggles;

        return (
            <div>
                <Form validated={validated} onSubmit={onSubmit}>
                    {configToggles.showUserId && (
                        <>
                            <h1 className="form-heading">{t('settings.title')}</h1>
                            <Form.Group>
                                <Form.Label>{t('settings.userId')}</Form.Label>
                                <div className={'setting'}>
                                    <div>{userId}</div>
                                </div>
                            </Form.Group>
                        </>
                    )}
                    <Form.Group>
                        {configToggles.showContainerName && (
                            <>
                                <Form.Label>{t('settings.containerName')}</Form.Label>
                                <div className={'setting'}>
                                    <Form.Control
                                        required
                                        as="select"
                                        className={'form-control'}
                                        onChange={(e) => setContainerName(e.target.value)}
                                        value={containerName}
                                    >
                                        {Object.values(containers).map((container: ContainerAttributes) => (
                                            <option key={container.name} value={container.name}>
                                                {container.name}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </div>
                            </>
                        )}
                    </Form.Group>
                    <Form.Group>
                        {(configToggles.showTechnicalDetails || configToggles.showManualValidation) && (
                            <Form.Label>{t('settings.display')}</Form.Label>
                        )}
                        {configToggles.showTechnicalDetails && (
                            <div className={'setting'}>
                                <Form.Label>{t('settings.technicalDetails')}</Form.Label>
                                <ToggleSlider
                                    name="technicalDetails"
                                    checked={showTechnicalDetails}
                                    onChange={(e) => setShowTechnicalDetails(e.target.checked)}
                                />
                            </div>
                        )}
                        {configToggles.showManualValidation && (
                            <div className={'setting'}>
                                <Form.Label>{t('settings.manualValidation')}</Form.Label>
                                <ToggleSlider
                                    name="manualValidation"
                                    checked={showManualValidation}
                                    onChange={(e) => setShowManualValidation(e.target.checked)}
                                />
                            </div>
                        )}
                    </Form.Group>
                    <Form.Group>
                        {configToggles.showFieldSpecificSearch && <Form.Label>{t('settings.search')}</Form.Label>}
                        {configToggles.showFieldSpecificSearch && (
                            <div className={'setting'}>
                                <Form.Label>{t('settings.fieldSpecificSearch')}</Form.Label>
                                <ToggleSlider
                                    name="fieldSpecificSearch"
                                    checked={showFieldSpecificSearch}
                                    onChange={(e) => setShowFieldSpecificSearch(e.target.checked)}
                                />
                            </div>
                        )}
                    </Form.Group>
                    <div className={'settings-form-buttons'}>
                        <HibuTextButton disabled={false} label={t('general.cancel')} type="reset" onClick={onGoBack}/>
                        <HibuTextButton
                            disabled={false}
                            label={t('settings.submitButton')}
                            filled={true}
                            type="submit"
                        />
                    </div>
                    <div className={'settings-footer'}>
                        <div>hibu-ui : {indeksoUi}</div>
                        <div>hibu-ui-backend : {indeksoUiBackendVersion}</div>
                        <div>hibu : {indeksoVersion}</div>
                    </div>
                </Form>
            </div>
        );
    };

    return (
        <div id="settings-component">
            <MessageBox id={MESSAGE_BOX_SETTINGS_ID}/>
            {getContent()}
        </div>
    );
});
